/**
 * THE SINGLE SOURCE OF TRUTH for MIQ's number-format locale partition.
 *
 * WHY HERE: `@miq/utils` is the BOTTOM of the strict one-way library chain
 * (utils -> shared-models -> platform -> platform-ui). The partition used to be duplicated in
 * `projects/miq/shared-models/src/lib/constants/ce-locales.const.ts` (arrays) AND in
 * `projects/miq/utils/src/lib/misc/locale-utils.ts` (Sets). `@miq/shared-models` imports
 * `@miq/utils` (see its composers) and `@miq/utils` NEVER imports `@miq/shared-models`, so the
 * constant can only live down here; the shared-models file is now a re-export shim. Do not add an
 * import of `@miq/shared-models` to this library to "move it back" — that would make the two
 * libraries mutually dependent at runtime.
 *
 * THIS FILE MUST HAVE NO IMPORTS. `locale-utils.ts` imports it (for its Sets) and
 * `locale-number.ts` imports both, so any import added here risks a cycle whose const
 * initializers would hit a TDZ error depending on which module the bundler enters first.
 *
 * THE PRODUCT RULE (keyed off the USER'S locale, never the data's country):
 *
 *   | group             | locales                                                     | 1234.56    |
 *   |-------------------|-------------------------------------------------------------|------------|
 *   | space + comma     | pl-PL fi-FI hu-HU nb-NO sv-SE pt-PT cs-CZ sk-SK fr-*        | 1 234,56   |
 *   | dot + comma       | de-DE de-AT it-IT nl-NL da-DK es-ES ro-RO sr-RS nl-BE de-CH | 1.234,56   |
 *   | comma + dot       | EVERYTHING ELSE (en-* zh-* ko-*)                            | 1,234.56   |
 *
 * The partition deliberately DIVERGES FROM CLDR in both directions, which is why the override
 * layer in `locale-number.ts` exists and cannot be deleted:
 *   - de-AT / de-CH / it-CH: CLDR groups with U+00A0 / U+2019, the rule demands a DOT.
 *   - pt-PT: CLDR (and Angular's `pt` fallback) groups with a DOT, the rule demands a SPACE.
 */

/** Space (U+00A0) thousands separator, comma decimal separator: `1 234,56`. */
export const CE_COMMA_SPACE_DECIMAL_LOCALES: string[] = [
  'pl-PL',
  'fi-FI',
  'hu-HU',
  'nb-NO',
  'sv-SE',
  'pt-PT',
  'cs-CZ',
  'sk-SK',
  'fr-FR',
  'fr-BE',
  'fr-LU',
  'fr-CH',
];

/** Dot thousands separator, comma decimal separator: `1.234,56`. */
export const CE_COMMA_DOT_DECIMAL_LOCALES: string[] = [
  'de-DE',
  'de-AT',
  'it-IT',
  'nl-NL',
  'da-DK',
  'es-ES',
  'ro-RO',
  'sr-RS',
  'nl-BE',
  'de-BE',
  'de-LU',
  'de-CH',
  'it-CH',
];

/**
 * The union of the two CE groups: every locale whose decimal separator is a COMMA.
 * Callers that need to know WHICH separator to use must not use this — use
 * {@link getLocaleNumberStyle} instead.
 */
export const CE_LOCALES: string[] = [...CE_COMMA_SPACE_DECIMAL_LOCALES, ...CE_COMMA_DOT_DECIMAL_LOCALES];

/** `Set` views of the two halves, for hot-path membership tests. */
export const CE_SPACE_COMMA_LOCALES: ReadonlySet<string> = new Set(CE_COMMA_SPACE_DECIMAL_LOCALES);
export const CE_DOT_COMMA_LOCALES: ReadonlySet<string> = new Set(CE_COMMA_DOT_DECIMAL_LOCALES);
const CE_LOCALE_SET: ReadonlySet<string> = new Set(CE_LOCALES);

/**
 * Which of the three separator conventions a locale uses. Named `<thousands>-<decimal>`.
 */
export type MiqNumberStyle = 'space-comma' | 'dot-comma' | 'comma-dot';

/**
 * The three space characters that can appear as a thousands separator, written as ESCAPES because
 * a literal U+00A0 is indistinguishable from a plain space in a source file — a difference that
 * silently breaks both formatting and the tests that check it.
 *
 * U+0020 SPACE, U+00A0 NO-BREAK SPACE (what CLDR and MIQ display use), U+202F NARROW NO-BREAK
 * SPACE (what CLDR gives for `fr-*`).
 */
export const PLAIN_SPACE = '\u0020';
export const NBSP = '\u00A0';
export const NARROW_NBSP = '\u202F';
export const SPACE_SEPARATOR_VARIANTS = [PLAIN_SPACE, NBSP, NARROW_NBSP] as const;
const SPACE_VARIANT_SET: ReadonlySet<string> = new Set<string>(SPACE_SEPARATOR_VARIANTS);

/**
 * Every character CLDR uses as a grouping separator in a locale MIQ can reach. U+2019 RIGHT SINGLE
 * QUOTATION MARK is what CLDR groups `de-CH` / `it-CH` with.
 */
const GROUPING_CHARS: ReadonlySet<string> = new Set<string>([...SPACE_SEPARATOR_VARIANTS, '.', ',', '\u2019', "'"]);

export function isSpaceVariant(ch: string): boolean {
  return SPACE_VARIANT_SET.has(ch);
}

export function isGroupingChar(ch: string): boolean {
  return GROUPING_CHARS.has(ch);
}

/**
 * `true` when `locale` is one of the CE locales, i.e. its DECIMAL separator is a comma.
 *
 * Takes the locale EXPLICITLY. Almost every caller wants the current user's locale instead — use
 * `isCELocale()` from `./locale-number.ts`, which defaults to it. This variant exists for the call
 * sites that already hold a resolved locale in hand.
 */
export function isCELocaleCode(locale: string): boolean {
  return CE_LOCALE_SET.has(locale);
}

/**
 * Resolves a locale to its MIQ separator convention.
 *
 * `'comma-dot'` is the EXPLICIT default, so `en-US` (and every other non-CE locale) can never fall
 * through into a CE branch. That fall-through is a live bug in the legacy
 * `formatNumberValueForLocale` (`locale-utils.ts`), which returns `1 234,56` for US users.
 */
export function getLocaleNumberStyle(locale: string): MiqNumberStyle {
  if (CE_SPACE_COMMA_LOCALES.has(locale)) {
    return 'space-comma';
  }
  if (CE_DOT_COMMA_LOCALES.has(locale)) {
    return 'dot-comma';
  }
  return 'comma-dot';
}

export interface MiqNumberSeparators {
  /** Thousands separator. */
  group: string;
  /** Decimal separator. */
  decimal: string;
}

/**
 * The separators MIQ requires for a locale.
 *
 * @param plainSpace emit U+0020 instead of U+00A0 for the space group (export/CSV paths, where a
 * no-break space can survive into a spreadsheet cell and stop it parsing as a number).
 */
export function getMiqNumberSeparators(locale: string, plainSpace = false): MiqNumberSeparators {
  switch (getLocaleNumberStyle(locale)) {
    case 'space-comma':
      return { group: plainSpace ? PLAIN_SPACE : NBSP, decimal: ',' };
    case 'dot-comma':
      return { group: '.', decimal: ',' };
    default:
      return { group: ',', decimal: '.' };
  }
}
